import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, Briefcase } from "lucide-react";

const LINKS = [
  { to: "/about", label: "About RCS" },
  { to: "/brands", label: "Our Brands" },
  { to: "/leadership", label: "Leadership" },
  { to: "/journey", label: "Our Journey" },
  { to: "/team", label: "Team" },
  { to: "/careers", label: "Careers" },
  { to: "/news", label: "News & Insights" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 bg-navy-950/90 backdrop-blur-xl border-b border-gold/10 transition-shadow duration-300 ${scrolled ? "shadow-[0_10px_40px_-15px_rgba(0,0,0,0.6)]" : ""}`}
    >
      <div className="container-rcs flex h-24 items-center justify-between gap-6">
        <Link to="/" data-testid="nav-logo" aria-label="Roy Career Solutions Private Limited — Home" className="mr-8 flex shrink-0 items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-gold rounded-sm">
          <img src="/rcs-logo.png" alt="Roy Career Solutions Private Limited" className="h-[72px] w-[126px] max-w-none shrink-0 sm:h-20 sm:w-[140px]" />
        </Link>

        <nav aria-label="Primary" className="hidden xl:flex items-center gap-5">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `relative whitespace-nowrap text-[13px] font-semibold tracking-wide transition-colors duration-300 after:absolute after:-bottom-1.5 after:left-0 after:h-px after:bg-gold after:transition-all after:duration-300 ${
                  isActive ? "text-gold after:w-full" : "text-slate-300 hover:text-ivory after:w-0 hover:after:w-full"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link to="/brands" data-testid="nav-explore-brands-cta" className="btn-gold whitespace-nowrap !px-5 !py-2.5 !text-xs">
            Explore Our Brands <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
          </Link>
          <Link to="/portal" data-testid="nav-employee-workspace-cta" className="btn-ivory whitespace-nowrap !px-5 !py-2.5 !text-xs">
            <Briefcase className="h-3.5 w-3.5" aria-hidden /> Employee Workspace
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="xl:hidden text-ivory p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold rounded-sm"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <nav aria-label="Mobile" data-testid="nav-mobile-menu" className="xl:hidden border-t border-white/10 bg-navy-950/95 backdrop-blur-xl">
          <div className="container-rcs flex flex-col py-6 gap-1">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-mobile-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `rounded-md px-3 py-3 text-sm font-semibold transition-colors ${isActive ? "text-gold bg-white/5" : "text-slate-300 hover:text-ivory hover:bg-white/5"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/brands" data-testid="nav-mobile-explore-brands-cta" className="btn-gold mt-4 justify-center">
              Explore Our Brands <ArrowUpRight className="h-4 w-4" aria-hidden />
            </Link>
            <Link to="/portal" data-testid="nav-mobile-employee-workspace-cta" className="btn-ivory mt-2 justify-center">
              <Briefcase className="h-4 w-4" aria-hidden /> Employee Workspace
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
}
